// Celebration effects utilities - sound and haptics
import { BrandVariant } from '@/lib/brandConfig';

/**
 * Play a sizzling steak sound effect (BBQ brand)
 */
const playBBQSizzle = (audioContext: AudioContext) => {
  const duration = 2.5; // Extended for clear recognition
  const bufferSize = audioContext.sampleRate * duration;
  const buffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
  const data = buffer.getChannelData(0);
  
  // Generate sizzle noise with crackle variations
  for (let i = 0; i < bufferSize; i++) {
    const t = i / audioContext.sampleRate;
    // Slower envelope decay for longer audible sizzle
    const envelope = Math.exp(-t * 0.8) * 0.7 + 0.3 * Math.exp(-t * 2);
    // Add random crackles - slightly more frequent
    const crackle = Math.random() < 0.025 ? Math.random() * 0.4 : 0;
    // Combine noise with subtle frequency variations
    data[i] = (Math.random() * 2 - 1) * envelope * 0.25 + crackle;
  }
  
  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  
  // High-pass filter for sizzle character
  const highPass = audioContext.createBiquadFilter();
  highPass.type = 'highpass';
  highPass.frequency.value = 2500;
  
  // Add some warmth with low-pass
  const lowPass = audioContext.createBiquadFilter();
  lowPass.type = 'lowpass';
  lowPass.frequency.value = 7000;
  
  const gainNode = audioContext.createGain();
  gainNode.gain.setValueAtTime(0.35, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
  
  source.connect(highPass);
  highPass.connect(lowPass);
  lowPass.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  source.start();
  source.stop(audioContext.currentTime + duration);
};

/**
 * Play a brand-specific celebration sound effect using Web Audio API
 */
export const playCelebrationSound = (brand?: BrandVariant) => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    // BBQ only - always play sizzle
    playBBQSizzle(audioContext);
  } catch (error) {
    console.log('Audio playback not available:', error);
  }
};

/**
 * Trigger haptic feedback on supported devices
 * Uses different patterns for different effects
 */
export const triggerHapticFeedback = (type: 'success' | 'light' | 'medium' = 'success') => {
  try {
    if ('vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [20],
        success: [30, 50, 30, 50, 50], // Celebration pattern
      };
      navigator.vibrate(patterns[type]);
    }
  } catch (error) {
    console.log('Haptic feedback not available:', error);
  }
};

/**
 * Combined celebration effect - sound + haptics
 * @param brand - Optional brand variant for brand-specific sounds
 */
export const triggerCelebration = (brand?: BrandVariant) => {
  playCelebrationSound(brand);
  triggerHapticFeedback('success');
};

export default triggerCelebration;
